import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2,
  ShieldCheck,
  ArrowLeft,
  Search,
  RotateCcw,
  Save,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  BookOpen,
  LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABILITY_TYPES = [
  { value: 'direct', label: 'Direct (buy on site)', tone: 'green' },
  { value: 'external', label: 'External (Amazon only)', tone: 'amber' },
  { value: 'unavailable', label: 'Unavailable', tone: 'zinc' }
];

const TONE_STYLES = {
  green: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  zinc: 'bg-zinc-500/10 border-zinc-500/30 text-zinc-300'
};

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' };
};

const AdminBooksPage = () => {
  const { isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editing, setEditing] = useState(null); // product_id being edited
  const [savingId, setSavingId] = useState(null);
  const [successId, setSuccessId] = useState(null);

  // ---- Auth gate (same pattern as /admin) ----
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?redirect=/admin/books', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/me`, { headers: authHeaders() });
        if (res.status === 401) {
          navigate('/login?redirect=/admin/books', { replace: true });
          return;
        }
        const data = await res.json();
        if (!data.is_admin) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setAdminInfo(data);
      } catch (e) {
        setError(e.message || 'Failed to verify admin access');
      } finally {
        setChecking(false);
      }
    };
    if (isAuthenticated) checkAdmin();
  }, [isAuthenticated, navigate]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/books/availability`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminInfo) loadProducts();
  }, [adminInfo, loadProducts]);

  const filtered = useMemo(() => {
    if (!searchQuery) return products;
    const q = searchQuery.toLowerCase();
    return products.filter((p) =>
      (p.product_id || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  const saveOverride = async (productId, draft) => {
    setSavingId(productId);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/books/availability/${encodeURIComponent(productId)}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(draft)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      await loadProducts();
      setEditing(null);
      setSuccessId(productId);
      setTimeout(() => setSuccessId(null), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const revertOverride = async (productId) => {
    if (!window.confirm(`Revert '${productId}' to its code default?`)) return;
    setSavingId(productId);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/books/availability/${encodeURIComponent(productId)}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadProducts();
      setEditing(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }
  if (!adminInfo) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-zinc-400 hover:text-white transition-colors"
              data-testid="admin-books-back"
              aria-label="Back to admin console"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight" data-testid="admin-books-heading">
                Book Availability
              </h1>
              <p className="text-xs text-zinc-500">Admin · {adminInfo.email}</p>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="admin-books-logout"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-red-400 font-semibold mb-1">ADMIN.1</p>
            <h2 className="text-2xl font-bold text-white mb-1">Manage product availability</h2>
            <p className="text-zinc-400 max-w-2xl text-sm">
              Override where each product can be purchased: direct on the site, external on Amazon, or unavailable.
              Overrides persist in <code className="text-xs bg-zinc-900 px-1.5 py-0.5 rounded">book_availability_overrides</code> and merge onto the static PRODUCTS dict at read time.
              Revert to restore the code default.
            </p>
          </div>
          <div className="text-sm text-zinc-500" data-testid="admin-books-count">
            {products.length} product{products.length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            placeholder="Search products by id or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
            data-testid="admin-books-search"
          />
        </div>

        {error ? (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-4 flex items-start gap-3" data-testid="admin-books-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">{error}</div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-white">×</button>
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-zinc-500" data-testid="admin-books-empty">No products match your search.</div>
        ) : (
          <div className="space-y-3" data-testid="admin-books-list">
            {filtered.map((p) => (
              <ProductRow
                key={p.product_id}
                product={p}
                editing={editing === p.product_id}
                saving={savingId === p.product_id}
                justSaved={successId === p.product_id}
                onStartEdit={() => setEditing(p.product_id)}
                onCancel={() => setEditing(null)}
                onSave={(draft) => saveOverride(p.product_id, draft)}
                onRevert={() => revertOverride(p.product_id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ---- Row ----

const ProductRow = ({ product, editing, saving, justSaved, onStartEdit, onCancel, onSave, onRevert }) => {
  const eff = product.effective || {};
  const typeMeta = AVAILABILITY_TYPES.find((t) => t.value === eff.availability_type) || AVAILABILITY_TYPES[2];

  // Local edit state
  const [draft, setDraft] = useState({
    available: eff.available,
    availability_type: eff.availability_type,
    amazon_kindle: eff.amazon_links?.kindle || '',
    amazon_paperback: eff.amazon_links?.paperback || '',
    kindle_exclusive_until: eff.kindle_exclusive_until || '',
    launch_date: eff.launch_date || ''
  });

  useEffect(() => {
    if (editing) {
      setDraft({
        available: eff.available,
        availability_type: eff.availability_type,
        amazon_kindle: eff.amazon_links?.kindle || '',
        amazon_paperback: eff.amazon_links?.paperback || '',
        kindle_exclusive_until: eff.kindle_exclusive_until || '',
        launch_date: eff.launch_date || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, product.product_id]);

  const handleSave = () => {
    const payload = {
      available: !!draft.available,
      availability_type: draft.availability_type,
      amazon_links: (draft.amazon_kindle || draft.amazon_paperback)
        ? {
            ...(draft.amazon_kindle ? { kindle: draft.amazon_kindle.trim() } : {}),
            ...(draft.amazon_paperback ? { paperback: draft.amazon_paperback.trim() } : {})
          }
        : null,
      kindle_exclusive_until: draft.kindle_exclusive_until || null,
      launch_date: draft.launch_date || null
    };
    // Omit null amazon_links (backend treats null as "don't update"); send empty object if user cleared both
    if (payload.amazon_links === null) {
      delete payload.amazon_links;
    }
    onSave(payload);
  };

  return (
    <div
      className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden"
      data-testid={`admin-book-row-${product.product_id}`}
    >
      <div className="p-4 flex flex-wrap items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-zinc-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <code className="text-xs bg-zinc-900 text-zinc-300 px-1.5 py-0.5 rounded border border-zinc-800">
              {product.product_id}
            </code>
            <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${TONE_STYLES[typeMeta.tone]}`}>
              {typeMeta.label.split(' ')[0]}
            </span>
            {product.override_applied ? (
              <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border bg-purple-500/10 border-purple-500/30 text-purple-300">
                Overridden
              </span>
            ) : null}
            {justSaved ? (
              <span className="flex items-center gap-1 text-emerald-400 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" /> Saved
              </span>
            ) : null}
          </div>
          <p className="text-white font-medium line-clamp-1">{product.name || product.product_id}</p>
          {product.description ? (
            <p className="text-zinc-500 text-xs line-clamp-1 mt-0.5">{product.description}</p>
          ) : null}
          {eff.amazon_links && (eff.amazon_links.kindle || eff.amazon_links.paperback) ? (
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              {eff.amazon_links.kindle ? (
                <a href={eff.amazon_links.kindle} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-amber-400 hover:text-amber-300 truncate max-w-xs">
                  <ExternalLink className="w-3 h-3" /> Kindle
                </a>
              ) : null}
              {eff.amazon_links.paperback ? (
                <a href={eff.amazon_links.paperback} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-amber-400 hover:text-amber-300 truncate max-w-xs">
                  <ExternalLink className="w-3 h-3" /> Paperback
                </a>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {product.override_applied && !editing ? (
            <button
              onClick={onRevert}
              disabled={saving}
              className="flex items-center gap-1 text-xs px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg disabled:opacity-50"
              data-testid={`admin-book-revert-${product.product_id}`}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Revert
            </button>
          ) : null}
          {!editing ? (
            <button
              onClick={onStartEdit}
              className="text-xs px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg"
              data-testid={`admin-book-edit-${product.product_id}`}
            >
              Edit
            </button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="border-t border-zinc-800 bg-zinc-950/60 p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`admin-book-edit-panel-${product.product_id}`}>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Availability type</label>
            <select
              value={draft.availability_type || 'direct'}
              onChange={(e) => setDraft({ ...draft, availability_type: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              data-testid={`admin-book-availability-type-${product.product_id}`}
            >
              {AVAILABILITY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <input
              id={`avail-${product.product_id}`}
              type="checkbox"
              checked={!!draft.available}
              onChange={(e) => setDraft({ ...draft, available: e.target.checked })}
              className="w-4 h-4 accent-purple-500"
              data-testid={`admin-book-available-${product.product_id}`}
            />
            <label htmlFor={`avail-${product.product_id}`} className="text-sm text-zinc-200">
              Available (shown in public product list)
            </label>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Amazon Kindle URL</label>
            <input
              type="url"
              placeholder="https://www.amazon.co.uk/dp/..."
              value={draft.amazon_kindle}
              onChange={(e) => setDraft({ ...draft, amazon_kindle: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              data-testid={`admin-book-kindle-url-${product.product_id}`}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Amazon Paperback URL</label>
            <input
              type="url"
              placeholder="https://www.amazon.co.uk/dp/..."
              value={draft.amazon_paperback}
              onChange={(e) => setDraft({ ...draft, amazon_paperback: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              data-testid={`admin-book-paperback-url-${product.product_id}`}
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">
              Kindle exclusive until (optional)
            </label>
            <input
              type="date"
              value={(draft.kindle_exclusive_until || '').slice(0, 10)}
              onChange={(e) => setDraft({ ...draft, kindle_exclusive_until: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">
              Launch date (optional)
            </label>
            <input
              type="date"
              value={(draft.launch_date || '').slice(0, 10)}
              onChange={(e) => setDraft({ ...draft, launch_date: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
            <button
              onClick={onCancel}
              disabled={saving}
              className="text-sm px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg disabled:opacity-50"
              data-testid={`admin-book-cancel-${product.product_id}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 text-sm px-4 py-2 bg-gradient-to-r from-purple-600 to-red-600 text-white rounded-lg disabled:opacity-50"
              data-testid={`admin-book-save-${product.product_id}`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save override
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminBooksPage;
